/**
 * Permission-filtered placement nav metadata.
 * Icons are resolved in roleNavConfig via iconKey.
 */

import {
  canViewPlacementOverview,
  canManageMockCampaigns,
  canConductMockInterview,
  canViewCoachingInsights,
  canViewCoachingStudents,
  canViewPlacementBatchAnalytics,
  canCurateCompanyIntel,
} from './placementPermissions';
import {
  placementOverviewPath,
  placementCampaignsPath,
  placementStudentsPath,
  placementCoachingInsightsPath,
  placementInterviewsPath,
  studentPlacementHubPath,
  studentPlacementCompaniesPath,
  studentPlacementProfilePath,
  facultyPreparationWorkspacePath,
  companyIntelligenceConsolePath,
  studentMockInterviewsPath,
  studentCareerSignalsPath,
} from './placementPaths';
import { isFacultyJourneyUiEnabled, isMockInterviewsEnabled } from '../config/placementInsightsFlags';

/**
 * Coordinator / faculty placement nav (permission-gated).
 * @param {object} user
 * @returns {Array<{ id: string, label: string, path: string, iconKey: string }>}
 */
export function getCoordinatorPlacementNavMeta(user) {
  if (!user) return [];

  const items = [];

  if (canViewPlacementOverview(user)) {
    items.push({
      id: 'placement-overview',
      label: 'Overview',
      path: placementOverviewPath(user),
      iconKey: 'dashboard',
    });
  }

  if (canViewPlacementBatchAnalytics(user) && isFacultyJourneyUiEnabled()) {
    items.push({
      id: 'placement-prepare',
      label: 'Prepare for Companies',
      path: facultyPreparationWorkspacePath(user),
      iconKey: 'assignment',
    });
  }

  if (canManageMockCampaigns(user) && isMockInterviewsEnabled()) {
    items.push({
      id: 'placement-campaigns',
      label: 'Mock Interviews',
      path: placementCampaignsPath(user),
      iconKey: 'placement',
    });
  }

  if (canViewCoachingStudents(user)) {
    items.push({
      id: 'placement-students',
      label: user.role === 'faculty' ? 'Students to Coach' : 'Students to Coach',
      path: placementStudentsPath(user),
      iconKey: 'people',
    });
  }

  if (canViewCoachingInsights(user)) {
    items.push({
      id: 'placement-coaching-insights',
      label: 'Coaching Insights',
      path: placementCoachingInsightsPath(user),
      iconKey: 'assessment',
    });
  }

  if (canCurateCompanyIntel(user)) {
    items.push({
      id: 'placement-company-intelligence',
      label: 'Data curation',
      path: companyIntelligenceConsolePath(user),
      iconKey: 'placement',
    });
  }

  if (canConductMockInterview(user) && isMockInterviewsEnabled()) {
    items.push({
      id: 'placement-interviews',
      label: user.role === 'admin' ? 'Interview queue' : 'My Interviews',
      path: placementInterviewsPath(user),
      iconKey: 'grade',
    });
  }

  return items;
}

/**
 * Student placement hub nav (fixed set — student role only).
 * @returns {Array<{ id: string, label: string, path: string, iconKey: string }>}
 */
export function getStudentPlacementNavMeta() {
  // Feedback lives on the Mock Interviews page (Upcoming + Released feedback).
  // A separate Feedback item used the same path and dual-highlighted the sidebar.
  const items = [
    { id: 'placement-hub', label: 'Readiness', path: studentPlacementHubPath(), iconKey: 'dashboard' },
    { id: 'placement-companies', label: 'Company Matches', path: studentPlacementCompaniesPath(), iconKey: 'placement' },
    { id: 'placement-profile', label: 'My Profile', path: studentPlacementProfilePath(), iconKey: 'profile' },
  ];
  if (isMockInterviewsEnabled()) {
    items.push({
      id: 'placement-interviews',
      label: 'Mock Interviews',
      path: studentMockInterviewsPath(),
      iconKey: 'grade',
    });
  }
  items.push({
    id: 'placement-insights',
    label: 'Career Signals',
    path: studentCareerSignalsPath(),
    iconKey: 'assessment',
  });
  return items;
}

/** Section title for sidebar grouping. */
export function getPlacementNavSectionTitle(user) {
  if (user?.role === 'student') return 'Placement Hub';
  return 'Placements';
}
